import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Search, Printer, Calendar, ArrowUp, Loader2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import { supabase } from '../../lib/supabase';

const TermsOfService = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [termsContent, setTermsContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [versionString, setVersionString] = useState('');

  useEffect(() => {
    fetchActiveTerms();

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchActiveTerms = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('seller_agreements_versions')
        .select('*')
        .eq('agreement_type', 'buyer')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setTermsContent(data.content);
        setVersionString(data.version_string);
        setLastUpdated(new Date(data.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }));
      } else {
        setError('No active terms of service found.');
      }
    } catch (err) {
      console.error('Error fetching terms:', err);
      setError('Failed to load terms of service. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service - OpenGadget</title>
        <meta
          name="description"
          content="OpenGadget Terms of Service - Comprehensive legal documentation for platform usage, user agreements, and policies."
        />
      </Helmet>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />

        <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header Section */}
            <div className="px-8 py-10 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Conditions of Use
                  </h1>
                  {versionString && (
                    <p className="text-sm text-gray-500 mb-2">Version {versionString}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Last updated: {lastUpdated || 'Loading...'}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print Document</span>
                </button>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 md:p-12">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                  <p className="text-gray-500">Loading terms and conditions...</p>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
                  <div>
                    <h3 className="text-red-900 font-semibold mb-1">Error Loading Content</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                    <button 
                      onClick={fetchActiveTerms}
                      className="mt-4 text-sm font-medium text-red-600 hover:text-red-800 underline"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="prose prose-blue max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: termsContent }}
                />
              )}
            </div>

            {/* Footer Contact Info */}
            <div className="px-8 py-8 bg-blue-50 border-t border-blue-100">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Questions About Our Terms?</h3>
                  <p className="text-sm text-gray-600">Our legal team is here to help with any clarifications.</p>
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href="mailto:legal@opengadget.com"
                    className="inline-flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Contact Legal Support
                  </a>
                  <Link to="/" className="text-sm text-blue-600 hover:underline">Return to Home</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} OpenGadget. All rights reserved.
          </div>
        </main>

        <Footer />

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50 animate-in fade-in slide-in-from-bottom-4"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );
};

export default TermsOfService;